/**
 * StickyHeader - Mobile Web Sticky Navigation (DD-261)
 *
 * Implements sticky header from v1 HTML prototype with navigation menu:
 * - Left: Menu icon (hamburger) - opens dropdown with History, Stats, Settings
 * - Center: "DramaDetector" gradient text
 * - Right: Spacer for layout balance
 *
 * Design Specs (from HTML prototype):
 * - Height: 48px
 * - Background: #1a1a2e with 90% opacity + backdrop blur
 * - Border: Bottom border with 10% white opacity
 * - Sticky positioning at top
 * - Icons: 24px touch targets (44px minimum for accessibility)
 *
 * Navigation Menu:
 * - Glass morphism dropdown below header
 * - History, Stats, Settings links
 * - Auto-closes on navigation or outside click
 *
 * Mobile Web Keyboard Fix:
 * - Uses Visual Viewport API offsetTop for dynamic positioning
 * - Position fixed on mobile web, sticky on desktop
 * - Follows viewport offset when keyboard opens/closes
 */

import { View, Text, TouchableOpacity, StyleSheet, Platform, Pressable } from "react-native";
import { isMobileWebBrowser } from "../utils/platform";
import { useState, useMemo } from 'react';
import { useRouter } from 'expo-router';
import { Ionicons } from '@expo/vector-icons';
import { BlurView } from 'expo-blur';
import { GradientText } from './GradientText';
import { useVisualViewport } from '../hooks/useVisualViewport';

interface StickyHeaderProps {
  /** Platform-specific: Web only by default */
  webOnly?: boolean;
}

// Navigation menu items
const MENU_ITEMS = [
  { label: 'History', icon: 'time-outline', route: '/history' },
  { label: 'Stats', icon: 'stats-chart-outline', route: '/stats' },
  { label: 'Settings', icon: 'settings-outline', route: '/settings' },
] as const;

export function StickyHeader({
  webOnly = true,
}: StickyHeaderProps) {
  const router = useRouter();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { offsetTop } = useVisualViewport();

  // Only render on web if webOnly is true
  if (webOnly && Platform.OS !== 'web') {
    return null;
  }

  const handleMenuToggle = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const handleNavigate = (route: string) => {
    setIsMenuOpen(false);
    router.push(route as any); // Type assertion for expo-router typed routes
  };

  // KEYBOARD FIX: Dynamic top position that follows visual viewport on mobile web
  const dynamicTop = useMemo(() => {
    if (Platform.OS !== 'web') return 0;
    return Math.max(0, offsetTop);
  }, [offsetTop]);

  // Use position fixed on mobile web to keep header visible when keyboard opens
  const isMobileWeb = Platform.OS === "web" && typeof window !== "undefined" && isMobileWebBrowser();
  
  return (
    <>
      <View style={[
        styles.container, 
        isMobileWeb && { position: 'fixed' as any, top: dynamicTop }
      ]}>
        {/* Backdrop Blur Background (web only) */}
        {Platform.OS === 'web' ? (
          <View style={[StyleSheet.absoluteFill, styles.webBackground]} />
        ) : (
          <BlurView intensity={10} style={StyleSheet.absoluteFill} />
        )}

        {/* Header Content */}
        <View style={styles.content}>
          {/* Left: Menu Icon - Opens navigation dropdown */}
          <TouchableOpacity
            style={styles.iconButton}
            accessibilityLabel="Menu"
            accessibilityRole="button"
            onPress={handleMenuToggle}
          >
            <Ionicons name={isMenuOpen ? "close" : "menu"} size={24} color="#FFFFFF" />
          </TouchableOpacity>

          {/* Center: "DramaDetector" Gradient Text */}
          <View style={styles.brandContainer}>
            <GradientText style={styles.brandText}>
              DramaDetector
            </GradientText>
          </View>

          {/* Right: Spacer for layout balance */}
          <View style={styles.iconButton} />
        </View>

        {/* Bottom Border */}
        <View style={styles.bottomBorder} />
      </View>

      {/* Navigation Dropdown Menu */}
      {isMenuOpen && (
        <>
          {/* Backdrop overlay - closes menu when clicked */}
          <Pressable
            style={styles.backdrop}
            onPress={() => setIsMenuOpen(false)}
            accessibilityLabel="Close menu"
          />

          {/* Menu Content */}
          <View style={[
            styles.menuContainer,
            isMobileWeb && { top: dynamicTop + 48 }  // Position relative to dynamic header
          ]}>
            {/* Glass background */}
            {Platform.OS === 'web' ? (
              <View style={[StyleSheet.absoluteFill, styles.menuBackground]} />
            ) : (
              <BlurView intensity={60} tint="dark" style={StyleSheet.absoluteFill} />
            )}

            {/* Menu Items */}
            {MENU_ITEMS.map((item) => (
              <TouchableOpacity
                key={item.route}
                style={styles.menuItem}
                onPress={() => handleNavigate(item.route)}
                accessibilityLabel={item.label}
                accessibilityRole="button"
              >
                <Ionicons name={item.icon as any} size={20} color="#FFFFFF" />
                <Text style={styles.menuItemText}>{item.label}</Text>
              </TouchableOpacity>
            ))}
          </View>
        </>
      )}
    </>
  );
}

const styles = StyleSheet.create({
  container: {
    height: 48,
    position: (Platform.OS === 'web' ? 'sticky' : 'relative') as any,
    top: 0,
    zIndex: 50,
    width: '100%',
  },
  webBackground: {
    backgroundColor: 'rgba(26, 26, 46, 0.9)', // #1a1a2e at 90% opacity
    ...(Platform.OS === 'web' && {
      backdropFilter: 'blur(10px)',
      WebkitBackdropFilter: 'blur(10px)', // Safari support
    } as any),
  },
  content: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
  },
  iconButton: {
    width: 44, // Minimum touch target for accessibility
    height: 44,
    alignItems: 'center',
    justifyContent: 'center',
  },
  brandContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  brandText: {
    fontSize: 16,
    fontWeight: '700',
    fontFamily: Platform.select({
      web: 'Plus Jakarta Sans, Inter, -apple-system, sans-serif',
      default: 'System',
    }),
    color: '#FFFFFF', // Fallback: Solid white for native, overridden by .text-gradient-brand on web
  },
  bottomBorder: {
    height: 1,
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
  },
  // Menu Dropdown Styles
  backdrop: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    zIndex: 40, // Below menu, above content
  },
  menuContainer: {
    position: 'absolute',
    top: 48, // Below header (will be overridden on mobile web)
    left: 16,
    width: 200,
    borderRadius: 12,
    overflow: 'hidden',
    borderWidth: 1,
    borderColor: 'rgba(255, 255, 255, 0.1)',
    zIndex: 60, // Above backdrop
    ...(Platform.OS === 'web' && {
      boxShadow: '0 8px 16px rgba(0, 0, 0, 0.4)',
    } as any),
  },
  menuBackground: {
    backgroundColor: 'rgba(26, 26, 46, 0.95)',
    ...(Platform.OS === 'web' && {
      backdropFilter: 'blur(20px)',
      WebkitBackdropFilter: 'blur(20px)',
    } as any),
  },
  menuItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 14,
    paddingHorizontal: 16,
    gap: 12,
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(255, 255, 255, 0.05)',
  },
  menuItemText: {
    fontSize: 15,
    fontWeight: '500',
    color: '#FFFFFF',
  },
});
